import { useState } from 'react';

import {
  Box,
  Tab,
  Card,
  Chip,
  Link,
  Tabs,
  Stack,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  CardHeader,
  Typography,
  TableContainer,
  TablePagination,
  CircularProgress,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { useGetCommunicationActivitiesQuery } from 'src/services/integration/integrations.service';
import {
  ICommunicationActivity,
  CommunicationMatchStatus,
  CommunicationActivityType,
} from 'src/services/integration/integrations.types';

import AssignActivityModal from './assign-activity-modal';

// ----------------------------------------------------------------------

const matchColor = (status: CommunicationMatchStatus): 'warning' | 'info' | 'success' | 'default' => {
  switch (status) {
    case CommunicationMatchStatus.UNMATCHED: return 'warning';
    case CommunicationMatchStatus.UNRESOLVED: return 'info';
    case CommunicationMatchStatus.MATCHED: return 'success';
    default: return 'default';
  }
};

const formatWhen = (a: ICommunicationActivity): string => {
  const ts = a.started_at || a.created_at;
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const formatDuration = (seconds?: number): string => {
  if (!seconds && seconds !== 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

export default function UnmatchedActivityView() {
  const { t } = useTranslate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selected, setSelected] = useState<ICommunicationActivity | null>(null);
  const [filter, setFilter] = useState<'all' | 'review' | 'matched'>('all');

  // 'review' maps to undefined -> backend default (everything not yet matched)
  const matchStatusByFilter = {
    all: 'all',
    matched: CommunicationMatchStatus.MATCHED,
    review: undefined,
  } as const;
  const matchStatus = matchStatusByFilter[filter];

  const { data, isLoading, isFetching } = useGetCommunicationActivitiesQuery({
    page: page + 1,
    per_page: rowsPerPage,
    match_status: matchStatus,
  });

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;

  return (
    <Card>
      <CardHeader
        title={t('integrations.communications.queue.title')}
        subheader={t('integrations.communications.queue.subtitle')}
      />

      <Tabs
        value={filter}
        onChange={(_e, v) => { setFilter(v); setPage(0); }}
        sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="all" label={t('integrations.communications.queue.filter_all')} />
        <Tab value="review" label={t('integrations.communications.queue.filter_review')} />
        <Tab value="matched" label={t('integrations.communications.queue.filter_matched')} />
      </Tabs>

      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('integrations.communications.queue.col_type')}</TableCell>
              <TableCell>{t('integrations.communications.queue.col_direction')}</TableCell>
              <TableCell>{t('integrations.communications.queue.col_number')}</TableCell>
              <TableCell>{t('integrations.communications.queue.col_when')}</TableCell>
              <TableCell>{t('integrations.communications.queue.col_duration')}</TableCell>
              <TableCell>{t('integrations.communications.queue.col_match')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>

          <TableBody>
            {(isLoading || isFetching) && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !isFetching && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('integrations.communications.queue.empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !isFetching && rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {row.activity_type === CommunicationActivityType.CALL
                    ? t('integrations.communications.queue.type_call')
                    : t('integrations.communications.queue.type_sms')}
                </TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{row.direction || '—'}</TableCell>
                <TableCell>{row.external_phone_number || '—'}</TableCell>
                <TableCell>{formatWhen(row)}</TableCell>
                <TableCell>
                  {row.activity_type === CommunicationActivityType.CALL ? formatDuration(row.duration_seconds) : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={matchColor(row.match_status)}
                    label={t(`integrations.communications.match.${row.match_status}`)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    {row.provider_deeplink && (
                      <Link href={row.provider_deeplink} target="_blank" rel="noopener" variant="caption">
                        {t('integrations.communications.queue.view_in_provider')}
                      </Link>
                    )}
                    <Button size="small" variant="outlined" onClick={() => setSelected(row)}>
                      {t('integrations.communications.queue.assign')}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Box>

      <AssignActivityModal
        open={!!selected}
        activity={selected}
        onClose={() => setSelected(null)}
        onSuccess={() => setSelected(null)}
      />
    </Card>
  );
}
